import { useEffect, useRef } from 'react';
import { datadogRum } from '@datadog/browser-rum';

const SAMPLE_INTERVAL_MS = 30_000;

interface MemoryInfo {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

const getMemory = (): MemoryInfo | null => {
  const memory = (performance as unknown as { memory?: MemoryInfo }).memory;
  return memory ?? null;
};

/**
 * Periodically reports the JS heap size to Datadog RUM as a custom action.
 * Only emits in browsers exposing `performance.memory` (Chromium-based).
 * Tags each sample with the calling view name and the session age in
 * minutes so dashboards can plot heap growth against session duration.
 */
export const useMemoryTelemetry = (view: string) => {
  const sessionStart = useRef<number>(Date.now());

  useEffect(() => {
    if (!getMemory()) {
      return;
    }

    const sample = () => {
      const memory = getMemory();
      if (!memory) {
        return;
      }
      const session_age_min = Math.floor((Date.now() - sessionStart.current) / 60_000);

      datadogRum.addAction('heap_sample', {
        view,
        used_mb: memory.usedJSHeapSize / (1024 * 1024),
        total_mb: memory.totalJSHeapSize / (1024 * 1024),
        limit_mb: memory.jsHeapSizeLimit / (1024 * 1024),
        session_age_min,
      });

      // Coarse DOM-growth heuristic: if these counts drift upward across a
      // session it usually means components were detached but the underlying
      // <video>/<canvas>/<img> elements weren't released.
      datadogRum.addAction('dom_sample', {
        view,
        videos: document.getElementsByTagName('video').length,
        canvases: document.getElementsByTagName('canvas').length,
        images: document.getElementsByTagName('img').length,
        session_age_min,
      });
    };

    sample();
    const id = window.setInterval(sample, SAMPLE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [view]);
};
